import { Link } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";
import { NAV } from "@/lib/content";
import { IMG } from "@/lib/images";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { LEGAL_ORDER, LEGAL_UI } from "@/lib/legal";
import { openCookiePreferences } from "@/components/CookieBanner";
import { BookConsultation } from "@/components/BookConsultation";

export const Footer = () => {
  const { c, lang, setLang } = useLang();
  const { config } = useSiteConfig();
  const legal = LEGAL_ORDER[lang] || LEGAL_ORDER.fr;
  const ui = LEGAL_UI[lang] || LEGAL_UI.fr;
  const socials = [
    { name: "Instagram", url: config.instagram, icon: Instagram },
    { name: "Pinterest", url: config.pinterest, icon: null },
    { name: "LinkedIn", url: config.linkedin, icon: Linkedin },
  ];
  return (
    <footer data-testid="site-footer" className="bg-olime-ink text-olime-bg">
      <div className="container-olime py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div className="lg:col-span-1">
            <img src={IMG.logoWhite} alt="O'Lime Design" className="h-14 w-auto" />
            <p className="mt-5 text-sm text-white/60 leading-relaxed whitespace-pre-line">{c.footer.desc}</p>
            <p className="mt-6 text-xs tracking-[0.22em] uppercase text-olime-sand whitespace-pre-line">{c.footer.tagline}</p>
            <div className="mt-8"><BookConsultation variant="light" testId="footer-book-cta" /></div>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-5">{c.footer.nav}</div>
            <ul className="space-y-3 text-sm text-white/70">
              {NAV.filter((n) => n.key !== "home").map((item) => (
                <li key={item.to}>
                  <Link to={item.to} data-testid={`footer-nav-${item.key}`} className="link-underline hover:text-white transition-colors duration-300">
                    {c.nav[item.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-5">{c.footer.social}</div>
            <ul className="space-y-3 text-sm text-white/70">
              {socials.filter((s) => (s.url || "").trim()).map((s) => (
                <li key={s.name}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.name.toLowerCase()}`} className="link-underline hover:text-white transition-colors duration-300 inline-flex items-center gap-2">
                    {s.icon && <s.icon size={14} />}
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
            <div className="eyebrow text-white/50 mt-8 mb-4">{c.footer.langs}</div>
            <div className="flex gap-4 text-sm">
              {["FR", "IT", "EN"].map((l) => (
                <button key={l} onClick={() => setLang(l.toLowerCase())} className={lang === l.toLowerCase() ? "text-white underline" : "text-white/60 hover:text-white"}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="eyebrow text-white/50 mb-5">{c.footer.info}</div>
            <ul className="space-y-3 text-sm text-white/70">
              {legal.map((o) => (
                <li key={o.type}>
                  <Link to={`/legal/${o.type}`} data-testid={`footer-legal-${o.type}`} className="link-underline hover:text-white transition-colors duration-300">{o.label}</Link>
                </li>
              ))}
              <li>
                <button onClick={openCookiePreferences} data-testid="footer-manage-cookies" className="link-underline hover:text-white transition-colors duration-300 text-left">
                  {ui.banner.manage}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40">
          <span>© {new Date().getFullYear()} O&rsquo;Lime Design — Marie Mazzone. {c.footer.rights}</span>
          <span className="tracking-[0.2em] uppercase">{c.footer.cities}</span>
        </div>
      </div>
    </footer>
  );
};
